// Las piezas de DISEÑO que el Agente toma de "Asistente v2" —
// ADR-2026-09-12-el-agente-toma-el-diseno-del-asistente-v2.
//
// El diseñador rediseñó el asistente de "Crear desde cero" (`/modo/asistente-v2`); acá
// vive lo que de ese diseño vale para el Agente (el chat general): los botones de
// esquina del encabezado, el gradiente del título y del botón de enviar, los verbos del
// título tipeado y las sugerencias del arranque. Son constantes y no copias de
// `className` sueltas para que las dos superficies del Agente (panel y pantalla) no se
// desincronicen al primer retoque.
//
// Lo que NO se trajo, a propósito: el switch Imagen/Video, formato y cantidad inline, el
// botón "Crear" y la card mobile de referencias. Eran de un asistente que sólo genera.

import { BadgeDollarSign, ClipboardCheck, FileSpreadsheet, Film, Image as ImageIcon, PackagePlus, type LucideIcon } from 'lucide-react';

/** Los pills de esquina del encabezado ("Nuevo chat" / "Historial de chats"): chrome
 *  gris sin sombra ni borde, para que no compitan con la acción principal. */
export const BOTON_ESQUINA =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-neutral-100 py-2 px-4 text-[13.5px] font-semibold text-[#02062BE6] transition-colors hover:bg-neutral-200';

/** El "⋯" del encabezado, del mismo gris que los pills de esquina. */
export const BOTON_REDONDO_GRIS =
  'inline-flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-[#02062BE6] transition-colors hover:bg-neutral-200';

/** El look del botón de volver del cascarón mobile (`Sidebar.tsx`): 48px de alto, blanco
 *  con blur, sombra y aro finito. Lo toman "Nuevo chat" y el "⋯" cuando viven en esa
 *  misma banda, así la fila queda homogénea. Sin ancho: el redondo pone `w-12` y el
 *  pill su padding. */
export const BOTON_FLOTANTE_MOBILE =
  'inline-flex h-12 shrink-0 items-center justify-center gap-1.5 rounded-full bg-white/95 text-[15px] font-medium text-brand-black/85 shadow-md ring-1 ring-black/[0.04] backdrop-blur-md transition-all active:opacity-50';

/** El gradiente azul de "Generar imagen", que en v2 también enciende el botón de
 *  enviar. Va en `backgroundImage`: una sola capa, que se atenúa con `opacity`. */
export const GRADIENTE_ENVIAR = 'linear-gradient(181.83deg, #2563F5 8.76%, #0436AE 90.56%)';

/** El gradiente del título (`bg-clip-text`): diagonal suave para que se note en una
 *  sola línea, con el azul de marca en el medio. */
export const GRADIENTE_TITULO = 'linear-gradient(105deg, #101C63 0%, #1644EB 52%, #4F8DFF 100%)';

/** Los verbos del título tipeado ("… sin límites"). Los de v2 eran sólo de crear; el
 *  Agente también pregunta, analiza y planifica. Todos en voseo y legibles seguidos de
 *  "sin límites". */
export const VERBOS_AGENTE = [
  'Creá',
  'Preguntá',
  'Analizá',
  'Planificá',
  'Imaginá',
  'Diseñá',
  'Investigá',
] as const;

/**
 * La bienvenida de la PRIMERA VEZ — ADR-2026-09-14-la-bienvenida-del-agente.
 *
 * Sólo la ve quien todavía no tuvo ningún chat, y ocupa el lugar del título tipeado: a
 * alguien que nunca abrió el Agente "Creá sin límites" no le dice qué es esto, y apilarle
 * las dos cosas le compite la atención al mensaje que sí explica.
 *
 * Se presenta y ENUMERA, que es lo que hace falta la primera vez: las cuatro capacidades no
 * son decoración sino el mapa de lo que se puede pedir, y son de cuatro familias distintas a
 * propósito (imagen, video, datos, estrategia) para que no se lea como "esto hace fotos".
 *
 * Es casi lo mismo que `SALUDO_DE_LA_MASCOTA`, el tooltip del hover, y por eso ese saludo se
 * apaga mientras esta burbuja está: el mismo texto dos veces en la misma pantalla se lee
 * como un error, no como énfasis.
 */
export const BIENVENIDA_AGENTE =
  'Hola, soy el agente de Atlas. Te puedo ayudar con lo que necesites: crear imágenes y videos, analizar tu Instagram, o simplemente armar una estrategia para vender más.';

/**
 * Las sugerencias del arranque: un click COMPLETA la caja con `prompt` y deja el cursor
 * ahí; no manda el turno (ADR-2026-09-12-las-sugerencias-completan-la-caja). `label` es
 * lo que entra en el pill; `prompt` es un pedido entero, con el detalle que el agente
 * necesita para arrancar bien (qué, para dónde, en qué formato), escrito para que el
 * usuario lo retoque antes de mandarlo.
 *
 * Son **dos** y no cuatro: se fueron "Ideas para esta semana" y "Analizá mi competencia"
 * (ADR-2026-09-22-las-sugerencias-del-agente-son-dos). Las dos que quedan son a lo que la
 * marca viene —fotos y video—, se leen de un vistazo en el teléfono sin scrollear, y son
 * las mismas dos que ofrece la barra de la home de los rubros con catálogo propio. Lo
 * demás se le pide escribiéndolo, que es lo que la caja invita a hacer.
 */
export const SUGERENCIAS_AGENTE: { key: string; label: string; prompt: string; Icon: LucideIcon }[] = [
  {
    key: 'producto',
    label: 'Fotos de mi producto',
    prompt:
      'Quiero fotos profesionales de mi producto para la tienda online. Buscalo en mi catálogo y proponeme tres tomas distintas (una packshot con fondo neutro, una en uso y una de detalle) antes de generar nada.',
    Icon: ImageIcon,
  },
  {
    key: 'video',
    label: 'Un video para redes',
    prompt:
      'Hacé un video corto de mi producto para redes, vertical (9:16) y de unos 10 segundos, que arranque con un gancho fuerte y cierre con un llamado a la acción. Proponeme primero el guion toma por toma y después lo generamos.',
    Icon: Film,
  },
];

/** Una sugerencia: completa la caja con `prompt` (no manda). */
export type SugerenciaDelAgente = (typeof SUGERENCIAS_AGENTE)[number];

/**
 * Sugerencias de la PANTALLA desde la que se abrió el agente — ADR-2026-10-06-sugerencias-de-tienda-online.
 *
 * Las generales (fotos y video) no van en el panel del costado: ahí sólo ocupaban lugar (pedido
 * de Matías del 2026-09-18). Éstas son otra cosa: son lo que se puede hacer EN la pantalla que la
 * persona tiene abierta, así que sí van en el panel. La clave es el nombre de la pantalla que
 * arma `pantallaDeRuta` (el mismo que dice "Estás en …").
 *
 * Sólo cosas que el agente sabe hacer de verdad con sus herramientas (`ver_lanzamiento`,
 * `editar_lanzamiento`, `agregar_a_lanzamiento`). Nada de ventas ni métricas: el agente todavía
 * no las puede leer, y una sugerencia que no sabe contestar enseña a no usarlas.
 */
const REVISAR: SugerenciaDelAgente = {
  key: 'que-falta',
  label: '¿Qué le falta para publicar?',
  prompt:
    'Revisá mi lanzamiento y decime, producto por producto, qué le falta para poder publicarlo en mi tienda. Ordenalos de lo más urgente a lo menos.',
  Icon: ClipboardCheck,
};
const SUMAR: SugerenciaDelAgente = {
  key: 'sumar-catalogo',
  label: 'Sumar productos del catálogo',
  prompt:
    'Quiero sumar productos de mi catálogo a mi lanzamiento. Mostrame cuáles tengo que todavía no están adentro y elegimos juntos.',
  Icon: PackagePlus,
};
const PLANILLA: SugerenciaDelAgente = {
  key: 'planilla',
  label: 'Cargar desde una planilla',
  prompt:
    'Te voy a pasar una planilla con mis productos para cargarlos al lanzamiento. Decime qué columnas tiene que tener (el SKU es el que importa) y después te la adjunto.',
  Icon: FileSpreadsheet,
};
const PRECIOS: SugerenciaDelAgente = {
  key: 'precios',
  label: 'Ajustar precios',
  prompt:
    'Quiero ajustar los precios de mi lanzamiento. Mostrame los precios actuales y proponeme el cambio producto por producto antes de aplicarlo.',
  Icon: BadgeDollarSign,
};

export const SUGERENCIAS_POR_PANTALLA: Record<string, SugerenciaDelAgente[]> = {
  'Tienda online': [REVISAR, SUMAR, PLANILLA],
  'la ficha de un lanzamiento': [REVISAR, PRECIOS, SUMAR],
};
